export type StoryAcquisition = {
    source: string;
    referrerHost: string | null;
    campaign: string | null;
};

const MAX_SOURCE_LENGTH = 50;
const MAX_CAMPAIGN_LENGTH = 100;

const CROWN_DISCOVERY_SOURCES = [
    "crown_most",
    "crown_ascending",
    "crown_fresh",
] as const;
export type CrownDiscoverySource = typeof CROWN_DISCOVERY_SOURCES[number];
const CROWN_DISCOVERY_SOURCE_SET = new Set<string>(CROWN_DISCOVERY_SOURCES);

export function crownDiscoverySource(value: unknown): CrownDiscoverySource | null {
    return typeof value === "string" && CROWN_DISCOVERY_SOURCE_SET.has(value)
        ? value as CrownDiscoverySource
        : null;
}

export function buildStoryReaderHref(
    readerHrefBase: string,
    chapterId: string,
    source?: unknown,
): string {
    const params = new URLSearchParams({ chapter: chapterId });
    const crownSource = crownDiscoverySource(source);
    if (crownSource) params.set("utm_source", crownSource);
    return `${readerHrefBase}?${params.toString()}`;
}

function normalizedHost(value: string): string {
    return value.replace(/:\d+$/, "").replace(/^www\./, "").toLowerCase();
}

function referrerUrl(value: string | null): URL | null {
    if (!value) return null;
    try {
        return new URL(value);
    } catch {
        return null;
    }
}

function cleanCampaign(value: string | null): string | null {
    const cleaned = value?.trim().slice(0, MAX_CAMPAIGN_LENGTH);
    return cleaned || null;
}

function cleanSource(value: string | null): string | null {
    const cleaned = value
        ?.trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, MAX_SOURCE_LENGTH);
    return cleaned || null;
}

function classifyExternalHost(host: string): string {
    if (/(^|\.)instagram\.com$/.test(host)) return "instagram";
    if (/(^|\.)facebook\.com$/.test(host) || host === "fb.com") return "facebook";
    if (/(^|\.)reddit\.com$/.test(host)) return "reddit";
    if (/(^|\.)tiktok\.com$/.test(host)) return "tiktok";
    if (host === "t.co" || /(^|\.)(x|twitter)\.com$/.test(host)) return "x";
    if (
        /(^|\.)google\.[a-z.]+$/.test(host) ||
        /(^|\.)bing\.com$/.test(host) ||
        /(^|\.)duckduckgo\.com$/.test(host)
    ) {
        return "search";
    }
    return "external";
}

/**
 * Classifies a story landing without persisting a raw referrer URL. Query
 * strings and paths from external sites can contain private data, so only the
 * normalized host and explicitly supplied campaign name leave this function.
 */
export function buildStoryAcquisition(input: {
    params: URLSearchParams;
    referer: string | null;
    host: string;
    pathname: string;
}): StoryAcquisition {
    const campaign = cleanCampaign(input.params.get("utm_campaign"));
    const explicitSource = cleanSource(input.params.get("utm_source"));
    if (explicitSource) {
        return { source: explicitSource, referrerHost: null, campaign };
    }

    const parsedReferrer = referrerUrl(input.referer);
    if (!parsedReferrer) {
        return { source: "direct", referrerHost: null, campaign };
    }

    const ownHost = normalizedHost(input.host);
    const externalHost = normalizedHost(parsedReferrer.hostname);
    if (externalHost !== ownHost) {
        return {
            source: classifyExternalHost(externalHost),
            referrerHost: externalHost,
            campaign,
        };
    }

    const path = parsedReferrer.pathname.replace(/\/+$/, "") || "/";
    if (path === "/") return { source: "home", referrerHost: null, campaign };
    if (path === "/browse") return { source: "browse", referrerHost: null, campaign };
    return { source: "iloris", referrerHost: null, campaign };
}

export function isStoryReaderContinuation(input: {
    referer: string | null;
    host: string;
    pathname: string;
}): boolean {
    const parsedReferrer = referrerUrl(input.referer);
    if (!parsedReferrer) return false;
    return normalizedHost(parsedReferrer.hostname) === normalizedHost(input.host) &&
        (parsedReferrer.pathname.replace(/\/+$/, "") || "/") ===
            (input.pathname.replace(/\/+$/, "") || "/");
}

export function getAnalyticsWindow(days: number, now: Date = new Date()): {
    startDate: Date;
    startDay: string;
} {
    const safeDays = Math.max(1, Math.min(90, Math.trunc(days)));
    const startDate = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() - (safeDays - 1),
    ));
    return { startDate, startDay: startDate.toISOString().slice(0, 10) };
}

export function normalizeStoryAnalyticsDays(value: unknown): 7 | 30 {
    return value === 7 ? 7 : 30;
}

const STORY_SOURCE_BUCKETS = new Set([
    "direct",
    "home",
    "browse",
    "iloris",
    "instagram",
    "facebook",
    "reddit",
    "tiktok",
    "x",
    "search",
    "external",
    ...CROWN_DISCOVERY_SOURCES,
]);

export function canonicalStorySource(value: unknown): string {
    if (typeof value !== "string") return "direct";
    const normalized = cleanSource(value);
    return normalized && STORY_SOURCE_BUCKETS.has(normalized)
        ? normalized
        : "external";
}

export function storySourceLabel(source: string, referrerHost?: string | null): string {
    const labels: Record<string, string> = {
        direct: "Direct link",
        home: "Iloris home page",
        browse: "Browse page",
        iloris: "Other Iloris page",
        instagram: "Instagram",
        facebook: "Facebook",
        reddit: "Reddit",
        tiktok: "TikTok",
        x: "X / Twitter",
        search: "Search engine",
        external: referrerHost ? `External · ${referrerHost}` : "External site",
        crown_most: "Most Crowned",
        crown_ascending: "Ascending",
        crown_fresh: "Fresh Finds",
    };
    return labels[source] ?? source.replaceAll("_", " ").replace(/^./, (c) => c.toUpperCase());
}
